// =============================================================================
// Step 2 — Sign-up form
// =============================================================================
// Collects first name, last name, phone (required), and city. City is a
// type-to-search combobox over the full Israeli localities list (~1,300,
// sourced from data.gov.il / CBS). Submits to POST /api/supporters with the
// captured referrer slug. Phone is encrypted at rest (pgcrypto) server-side.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react"
import { Navigate } from "react-router-dom"
import { PageShell } from "@/components/brand/PageShell"
import { StepIndicator } from "@/components/brand/StepIndicator"
import { Button } from "@/components/ui/button"
import { ApiError, NetworkError, api, useApi } from "@/lib/api"
import { resolveRef, clearRef } from "@/lib/referrer"
import { track, sid } from "@/lib/track"
import { normalizePhone, isValidPhone } from "@/lib/phone"
import { ArrowLeft } from "lucide-react"

interface City {
  id: number
  name_he: string
  name_en: string | null
  district: string | null
}

interface CitiesResponse {
  cities: City[]
}

interface CreateSupporterResponse {
  supporter: { id: string; invite_slug: string }
  created: boolean
}

const INPUT_CLASS =
  "w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#1B2D52]/30 focus:border-[#1B2D52]"

export function SignupPage() {
  // Instrumentation only — see src/lib/track.ts. Closes the last blind spot:
  // reaching this page means Google let them through, so anything lost from
  // here is OURS and fixable by us.
  useEffect(() => {
    track("signup_view")
  }, [])


  const navigate = useNavigate()
  const location = useLocation()
  const apiCall = useApi()
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phone, setPhone] = useState("")
  const [cities, setCities] = useState<City[]>([])
  const [cityId, setCityId] = useState<number | "">("")
  const [cityQuery, setCityQuery] = useState("")
  const [cityOpen, setCityOpen] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Returning-supporter guard: if this signed-in user already completed signup
  // (e.g. an existing member re-linked across the Clerk dev->prod flip), skip
  // the form and send them to their status page.
  const { isLoaded: authLoaded, user } = useUser()
  const [alreadyMember, setAlreadyMember] = useState<boolean | null>(null)

  // Priority: the `?ref=` Clerk delivered post-OAuth (survives the browser hop),
  // then router state, then the same-browser localStorage/cookie fallbacks.
  const referrerSlug =
    resolveRef(location.search) ??
    (location.state as { referrerSlug?: string } | null)?.referrerSlug ??
    null

  // Did the browser keep our session across the hop to Google?
  //
  // The login page threads the sid that TAPPED into this page's query string.
  // If the sid we hold now is a different one, sessionStorage was dropped in
  // between — the person is the same, the identifier is not. Until 22 Aug that
  // produced two phantom half-journeys (a tap that "never returned" and a form
  // that "appeared from nowhere") and inflated the loss we blamed on Google.
  //
  // detail carries the ORIGINATING sid so the two halves can be stitched back
  // together in the query, not guessed at. Read-only, fire-and-forget, and it
  // touches nothing the user depends on.
  useEffect(() => {
    try {
      const from = new URLSearchParams(location.search).get("s")
      if (from && from !== sid()) track("sid_split", from)
    } catch {
      /* telemetry must never touch the user's path */
    }
  }, [location.search])

  useEffect(() => {
    api
      .get<CitiesResponse>("/api/cities")
      .then((d) => setCities(d.cities))
      .catch(() => setError("שגיאה בטעינת רשימת היישובים"))
  }, [])

  // Resolve whether the signed-in user is already a supporter so we can route
  // them to /status instead of re-showing the signup form.
  useEffect(() => {
    if (!authLoaded || !user) return
    apiCall<{ supporter: unknown }>("/api/me")
      .then((d) => setAlreadyMember(!!d.supporter))
      .catch(() => setAlreadyMember(false))
  }, [authLoaded, user, apiCall])

  // Prefill the name fields from the Google identity Clerk hands us after the
  // OAuth hop. Measured on the live prod instance (100 users): first_name is
  // present for 100%, last_name for 98% — phone and city for NONE, so those two
  // stay manual (Google's email+profile scopes never carry them).
  //
  // Rules: seed ONCE, only where Google actually gave us a value, and never over
  // anything already typed. These remain ordinary editable inputs — a prefilled
  // name is a suggestion the person can correct, not a locked field.
  const seededFromGoogle = useRef(false)
  useEffect(() => {
    if (!authLoaded || !user || seededFromGoogle.current) return
    seededFromGoogle.current = true
    if (user.firstName) setFirstName((cur) => cur || user.firstName || "")
    if (user.lastName) setLastName((cur) => cur || user.lastName || "")
  }, [authLoaded, user])

  // Filter the full localities list by what the user typed. Cap the rendered
  // matches so a broad query (or empty box) doesn't paint thousands of rows.
  const matches = useMemo(() => {
    const q = cityQuery.trim()
    const list = q ? cities.filter((c) => c.name_he.includes(q)) : cities
    return list.slice(0, 60)
  }, [cities, cityQuery])

  const phoneDigits = normalizePhone(phone)
  const phoneOk = isValidPhone(phoneDigits)
  const canSubmit =
    firstName.trim() && lastName.trim() && phoneOk && cityId !== "" && !submitting

  // Say WHY the button won't move. A disabled button that explains nothing is
  // indistinguishable from a broken site — users pressed it, nothing happened,
  // and they left. Only shown once they've started filling the form in.
  const blockedReason = (() => {
    if (submitting) return null
    if (!firstName.trim() || !lastName.trim()) return "צריך למלא שם פרטי ושם משפחה."
    if (!phone.trim()) return "צריך למלא מספר טלפון."
    if (!phoneOk) return "מספר הטלפון לא תקין — למשל 0541234567, או מספר מחו״ל עם קידומת מדינה כמו 15551234567+."
    if (cityId === "")
      return cityQuery.trim()
        ? "בחרו יישוב מתוך הרשימה שנפתחת (אפשר להתחיל להקליד ואז ללחוץ על השם)."
        : "צריך לבחור יישוב."
    return null
  })()

  function selectCity(c: City) {
    setCityId(c.id)
    setCityQuery(c.name_he)
    setCityOpen(false)
  }

  function onCityInput(value: string) {
    setCityQuery(value)
    setCityOpen(true)
    // Typing invalidates a prior pick — but DON'T strand the user. If what they
    // typed already identifies exactly one locality (or matches one exactly),
    // accept it. Before this, someone who typed their town's full name and never
    // clicked the dropdown was left with an empty cityId and a permanently dead
    // submit button that explained nothing — the single biggest cause of people
    // signing in with Google and never completing (2026-08-02).
    const q = value.trim()
    const exact = cities.find((c) => c.name_he === q)
    const partial = q ? cities.filter((c) => c.name_he.includes(q)) : []
    if (exact) setCityId(exact.id)
    else if (partial.length === 1) setCityId(partial[0].id)
    else setCityId("")
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await apiCall<CreateSupporterResponse>("/api/supporters", {
        method: "POST",
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phoneDigits,
          cityId: Number(cityId),
          referrerSlug: referrerSlug ?? undefined,
          marketingConsent,
        }),
      })
      track("signup_ok")
      clearRef()
      navigate("/status", { replace: true })
    } catch (e) {
      // Record WHY, not just that it failed. The 2026-08-03 incident was a
      // network error rendered in English to Hebrew users; had we been logging
      // the reason we would have seen it the same hour instead of days later.
      track(
        "signup_fail",
        e instanceof ApiError
          ? `api:${e.message}`
          : e instanceof NetworkError
            ? "network"
            : "unexpected",
      )
      // Only ever show OUR messages, which are Hebrew. A raw exception message
      // is browser-internal English ("Load failed" / "Failed to fetch") and is
      // useless to the person reading it — that exact string was shown to real
      // users mid-signup on 2026-08-03 and they gave up rather than retry.
      setError(
        e instanceof NetworkError || e instanceof ApiError
          ? e.message
          : "שגיאה לא צפויה. נסו שוב בעוד רגע.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  // Existing member (or still resolving) — don't flash the signup form.
  if (authLoaded && user && alreadyMember === null) {
    return (
      <PageShell bg="white">
        <div className="max-w-md mx-auto px-6 py-20 text-center text-sm text-[#6B7280]">
          טוען…
        </div>
      </PageShell>
    )
  }
  if (alreadyMember === true) return <Navigate to="/status" replace />

  return (
    <PageShell bg="white" header={<StepIndicator current={2} total={3} />}>
      <SignedOut>
        <Navigate to="/login" replace />
      </SignedOut>
      <SignedIn>
        <main className="max-w-md mx-auto px-6 lg:px-10 py-10">
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0F1B36] mb-1 leading-tight">
            ספר/י לנו מי את/ה
          </h1>
          <p className="text-sm text-[#4B5563] mb-6">
            שם פרטי, שם משפחה, טלפון ויישוב.
          </p>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#1B2D52] mb-1">שם פרטי</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                className={INPUT_CLASS}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1B2D52] mb-1">שם משפחה</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                className={INPUT_CLASS}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1B2D52] mb-1">טלפון</label>
              <input
                type="tel"
                inputMode="tel"
                dir="ltr"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                placeholder="050-1234567 או 1-555-1234567+"
                className={`${INPUT_CLASS} text-right`}
                required
              />
              {phone && !phoneOk && (
                <p className="mt-1 text-xs text-red-700">מספר טלפון לא תקין — מספר ישראלי, או מספר מחו״ל עם קידומת מדינה (למשל 15551234567+).</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1B2D52] mb-1">יישוב</label>
              <div className="relative">
                <input
                  type="text"
                  value={cityQuery}
                  onChange={(e) => onCityInput(e.target.value)}
                  onFocus={() => setCityOpen(true)}
                  onBlur={() => {
                    blurTimer.current = setTimeout(() => setCityOpen(false), 150)
                  }}
                  autoComplete="off"
                  placeholder="הקלידו שם יישוב…"
                  className={INPUT_CLASS}
                  aria-expanded={cityOpen}
                  role="combobox"
                  aria-autocomplete="list"
                  required
                />
                {cityOpen && matches.length > 0 && (
                  <ul
                    className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-[#E5E7EB] bg-white shadow-lg"
                    role="listbox"
                  >
                    {matches.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            if (blurTimer.current) clearTimeout(blurTimer.current)
                            selectCity(c)
                          }}
                          className="block w-full text-right px-3 py-2 text-sm hover:bg-[#1B2D52]/5 focus:bg-[#1B2D52]/5 focus:outline-none"
                        >
                          {c.name_he}
                          {c.district ? (
                            <span className="text-xs text-[#6B7280]"> · {c.district}</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {cityOpen && cityQuery.trim() && matches.length === 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#6B7280] shadow-lg">
                    לא נמצא יישוב בשם הזה
                  </div>
                )}
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-0.5 size-5 shrink-0 rounded border-[#D1D5DB] text-[#1B2D52] focus:ring-2 focus:ring-[#1B2D52]/30"
              />
              <span className="text-sm text-[#4B5563] leading-snug">
                אני מעוניין/ת לקבל עדכונים ומידע מאל הדגל בדוא״ל.{" "}
                <span className="text-[#9CA3AF]">(לא חובה)</span>
              </span>
            </label>
            {error && <p className="text-sm text-red-700 bg-red-50 rounded-lg p-2.5">{error}</p>}
            {blockedReason && (
              <p className="text-sm text-[#92400E] bg-[#FEF3C7] rounded-lg p-2.5">{blockedReason}</p>
            )}
            <Button type="submit" size="lg" disabled={!canSubmit} className="mt-2 group">
              {submitting ? "שולח…" : "הוסף/י את הדגל שלי"}
              <ArrowLeft className="size-5 transition-transform group-hover:-translate-x-1" />
            </Button>
          </form>
        </main>
      </SignedIn>
    </PageShell>
  )
}
